import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  AttendanceAnswer,
  AttendanceConfirmation as Confirmation,
  isAttendanceAnswer,
} from "@greenflagdate/shared";
import { apiReq } from "../../../lib/apiReq";
import { checkInQrSrc } from "../../../lib/checkInQrSrc";
import { formatEventWhen } from "../../../lib/formatEventWhen";
import { useHideNav } from "../../../lib/useHideNav";
import { Meta } from "../../../components/Meta/Meta";

const HEADINGS: Record<AttendanceAnswer, string> = {
  yes: "See you there!",
  no: "Thanks for letting us know",
};

const MESSAGES: Record<AttendanceAnswer, string> = {
  yes: "We'll let your host know you're coming.",
  no: "We'll let your host know. If your plans change, you're still welcome to come. Just show up and check in.",
};

const SWITCH_LABELS: Record<AttendanceAnswer, string> = {
  yes: "Actually, I Can't Make It",
  no: "Actually, I Can Come",
};

const other = (answer: AttendanceAnswer): AttendanceAnswer =>
  answer === "yes" ? "no" : "yes";

/**
 * The page the attendance email's Yes and No buttons open, at
 * `/attending/:token?answer=yes|no`. It records the answer in the link once
 * it loads (a POST, so a mail scanner that only fetches the link records
 * nothing), shows it, and lets them switch. Until the event is over it also
 * shows their check-in QR code, unless they said they can't make it; switching
 * back to yes brings it back. No sign-in: the token is the key.
 */
export const AttendanceConfirmation = () => {
  useHideNav();
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [req, reqState] = useReq<Confirmation>(apiReq);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [failed, setFailed] = useState(false);

  // Read once: after a switch the page, not the link, holds the answer.
  const [linkAnswer] = useState(() => searchParams.get("answer"));

  useEffect(() => {
    let current = true;
    const request = isAttendanceAnswer(linkAnswer)
      ? req.post(`/spark/attendance/${token}`, { answer: linkAnswer })
      : req.get(`/spark/attendance/${token}`);
    request.then((res) => {
      if (!current) return;
      if (res.success) setConfirmation(res.result);
      else setFailed(true);
    });
    return () => {
      current = false;
    };
  }, [req, token, linkAnswer]);

  const answer = async (next: AttendanceAnswer) => {
    const res = await req.post(`/spark/attendance/${token}`, { answer: next });
    if (res.success) {
      setConfirmation(res.result);
      // So a reload shows this answer rather than the one the link carried.
      setSearchParams(
        (params) => {
          params.set("answer", next);
          return params;
        },
        { replace: true },
      );
    }
  };

  if (failed) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg="bg.canvas"
        minH="100vh"
        px="1.5rem"
        gap="0.5rem"
      >
        <Heading textStyle="pageTitle" color="fg.default" textAlign="center">
          This link is no longer valid.
        </Heading>
        <Text textStyle="body" color="fg.muted" textAlign="center">
          If you canceled your registration, there is nothing more to do.
        </Text>
      </Flex>
    );
  }

  if (!confirmation) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const { spark } = confirmation;
  const eventName = spark.title || spark.activity;
  const recorded = confirmation.answer;

  return (
    <Flex
      direction="column"
      align="center"
      bg="bg.canvas"
      minH="100vh"
      px="1.5rem"
      py="2.5rem"
    >
      <Meta>
        <title>{`${eventName} | Green Flag`}</title>
      </Meta>
      <Flex
        direction="column"
        align="center"
        gap="1.25rem"
        w="100%"
        maxW="30rem"
        textAlign="center"
      >
        <Heading textStyle="hero" color="fg.default">
          {recorded
            ? HEADINGS[recorded]
            : `Are you still coming${confirmation.first_name ? `, ${confirmation.first_name}` : ""}?`}
        </Heading>

        <Box w="100%" bg="bg.subtle" borderRadius="lg" p="1rem">
          <Text textStyle="label" color="fg.default">
            {eventName}
          </Text>
          <Text textStyle="bodyLight" color="fg.muted">
            {formatEventWhen(spark.starts_at, { tonight: true })}
          </Text>
          {spark.location && (
            <Text textStyle="bodyLight" color="fg.muted">
              {spark.location}
            </Text>
          )}
        </Box>

        {!confirmation.can_answer ? (
          <Text textStyle="body" color="fg.muted">
            This event is over, so your answer can no longer change.
          </Text>
        ) : recorded ? (
          <>
            <Text textStyle="body" color="fg.muted">
              {MESSAGES[recorded]}
            </Text>
            <Button
              variant="outline"
              size="md"
              loading={reqState.loading}
              onClick={() => answer(other(recorded))}
            >
              {SWITCH_LABELS[recorded]}
            </Button>
          </>
        ) : (
          <>
            <Text textStyle="body" color="fg.muted">
              Your answer helps your host know who to expect. If your plans
              change, you're still welcome to come.
            </Text>
            <Flex gap="0.75rem" wrap="wrap" justify="center">
              <Button
                size="md"
                loading={reqState.loading}
                onClick={() => answer("yes")}
              >
                Yes, I'll Be There
              </Button>
              <Button
                variant="outline"
                size="md"
                loading={reqState.loading}
                onClick={() => answer("no")}
              >
                No, I Can't Make It
              </Button>
            </Flex>
          </>
        )}

        {confirmation.can_answer && recorded !== "no" && (
          <Flex
            direction="column"
            align="center"
            gap="0.75rem"
            w="100%"
            p="1rem"
            borderRadius="md"
            bg="greenLight.subtle"
          >
            <Box>
              <Text textStyle="sectionHeading" color="fg.default">
                Your check-in code
              </Text>
              <Text textStyle="finePrint" color="fg.muted" mt="0.125rem">
                Show this to your host when you arrive.
              </Text>
            </Box>
            <Box p="0.625rem" bg="bg.canvas" borderRadius="sm">
              <Image
                src={checkInQrSrc(confirmation.check_in_token)}
                alt="Your check-in QR code"
                boxSize="168px"
              />
            </Box>
          </Flex>
        )}

        <Button asChild variant="solid" size="md">
          <RouterLink to={`/event/atlanta/${spark.id}`}>
            View Event Details
          </RouterLink>
        </Button>
      </Flex>
    </Flex>
  );
};
